import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { SurveyConfigBlock, SurveyConfigBlockGroup } from '@highlight-cp/project-api';

import { initialState, VALIDATION_ERROR_TYPE } from './constants';

export const surveyDesignerSlice = createSlice({
  name: 'surveyDesigner',
  initialState,
  reducers: {
    addDeletingSurveyConfigBlockGroupId: (
      state,
      action: PayloadAction<{ surveyConfigId: string; surveyConfigBlockGroupId: string }>,
    ) => {
      const { surveyConfigId, surveyConfigBlockGroupId } = action.payload;
      const existingIds = state.deletingSurveyConfigBlockGroupIds[surveyConfigId] ?? [];
      if (existingIds.includes(surveyConfigBlockGroupId)) {
        return;
      }
      state.deletingSurveyConfigBlockGroupIds = {
        ...state.deletingSurveyConfigBlockGroupIds,
        [surveyConfigId]: [...existingIds, surveyConfigBlockGroupId],
      };
    },
    removeDeletingSurveyConfigBlockGroupId: (
      state,
      action: PayloadAction<{ surveyConfigId: string; surveyConfigBlockGroupId: string }>,
    ) => {
      const { surveyConfigId, surveyConfigBlockGroupId } = action.payload;
      const existingIds = state.deletingSurveyConfigBlockGroupIds[surveyConfigId] ?? [];
      if (existingIds.length === 0) {
        return;
      }
      state.deletingSurveyConfigBlockGroupIds = {
        ...state.deletingSurveyConfigBlockGroupIds,
        [surveyConfigId]: existingIds.filter((id) => id !== surveyConfigBlockGroupId),
      };
    },
    clearSurveyConfigBlocksSelection: (
      state,
      action: PayloadAction<{ surveyConfigId: string }>,
    ) => {
      const { [action.payload.surveyConfigId]: _removed, ...rest } =
        state.surveyConfigBlocksSelection;
      state.surveyConfigBlocksSelection = rest;
    },
    clearSurveyConfigBlockGroupsSelection: (
      state,
      action: PayloadAction<{ surveyConfigId: string }>,
    ) => {
      const { [action.payload.surveyConfigId]: _removed, ...rest } =
        state.surveyConfigBlockGroupsSelection;
      state.surveyConfigBlockGroupsSelection = rest;
    },
    /**
     * Deletes a survey config block or survey config block group display from the state
     */
    deleteSurveyConfigBlockDisplay: (
      state,
      action: PayloadAction<{
        surveyConfigId: string;
        surveyConfigBlockId: string;
      }>,
    ) => {
      const existingSurveyConfigBlocksDisplay = {
        ...state.surveyConfigBlocksDisplay[action.payload.surveyConfigId],
      };
      delete existingSurveyConfigBlocksDisplay[action.payload.surveyConfigBlockId];

      state.surveyConfigBlocksDisplay = {
        ...state.surveyConfigBlocksDisplay,
        [action.payload.surveyConfigId]: existingSurveyConfigBlocksDisplay,
      };
    },
    openBlockEditorTab: (state, action: PayloadAction<{ tab: string }>) => {
      state.activeBlockEditorTab = action.payload.tab;
    },
    setScrollToSurveyConfigBlockId: (state, action: PayloadAction<string>) => {
      state.scrollToSurveyConfigBlockId = action.payload;
    },
    setSelectedSurveyConfigBlock: (
      state,
      action: PayloadAction<{ scb: SurveyConfigBlock | null }>,
    ) => {
      state.activeSurveyConfigBlock = action.payload.scb;
      // Clear active group when a block is selected
      if (action.payload.scb) {
        state.activeSurveyConfigBlockGroup = null;
      }
    },
    setSelectedSurveyConfigBlockGroup: (
      state,
      action: PayloadAction<{ group: SurveyConfigBlockGroup | null }>,
    ) => {
      state.activeSurveyConfigBlockGroup = action.payload.group;
      // Clear active block when a group is selected
      if (action.payload.group) {
        state.activeSurveyConfigBlock = null;
      }
    },
    setIsExpandedSurveyConfigBlock: (
      state,
      action: PayloadAction<{
        surveyConfigId: string;
        surveyConfigBlockId: string;
        value: boolean;
      }>,
    ) => {
      state.surveyConfigBlocksDisplay = {
        ...state.surveyConfigBlocksDisplay,

        [action.payload.surveyConfigId]: {
          ...state.surveyConfigBlocksDisplay[action.payload.surveyConfigId],
          [action.payload.surveyConfigBlockId]: {
            ...state.surveyConfigBlocksDisplay?.[action.payload.surveyConfigId]?.[
              action.payload.surveyConfigBlockId
            ],
            isExpanded: action.payload.value,
          },
        },
      };
    },
    setIsExpandedSurveyBundle: (
      state,
      action: PayloadAction<{
        surveyConfigId: string;
        surveyBundleId: string;
        value: boolean;
      }>,
    ) => {
      state.surveyBundlesDisplay = {
        ...state.surveyBundlesDisplay,
        [action.payload.surveyConfigId]: {
          ...state.surveyBundlesDisplay[action.payload.surveyConfigId],
          [action.payload.surveyBundleId]: {
            ...state.surveyBundlesDisplay?.[action.payload.surveyConfigId]?.[
              action.payload.surveyBundleId
            ],
            isExpanded: action.payload.value,
          },
        },
      };
    },
    setOrderedSurveyConfigBlocks: (
      state,
      action: PayloadAction<{ blocks: SurveyConfigBlock[] }>,
    ) => {
      state.orderedSurveyConfigBlocks = action.payload.blocks;
    },
    setInputFocus: (state, action: PayloadAction<{ inputFocus: string }>) => {
      state.inputFocus = action.payload.inputFocus;
    },
    setIsSurveyConfigValid: (state, action: PayloadAction<{ isValid: boolean }>) => {
      state.isSurveyConfigValid = action.payload.isValid;
    },
    setInvalidSurveyConfigBlocks: (
      state,
      action: PayloadAction<{ blocks: SurveyConfigBlock[] }>,
    ) => {
      state.invalidSurveyConfigBlocks = action.payload.blocks;
    },
    setIsProjectLocked: (state, action: PayloadAction<{ isProjectLocked: boolean }>) => {
      state.isProjectLocked = action.payload.isProjectLocked;
    },
    setIsSkipLogicLoading: (state, action: PayloadAction<{ isLoading: boolean }>) => {
      state.isSkipLogicLoading = action.payload.isLoading;
    },
    setPreviewValidationError: (state) => {
      state.validationError = {
        hasError: true,
        validationErrorType: VALIDATION_ERROR_TYPE.PREVIEW,
      };
    },
    setProjectId: (state, action: PayloadAction<{ projectId: number }>) => {
      state.projectId = action.payload.projectId;
    },
    setSolutionId: (state, action: PayloadAction<{ solutionId: string }>) => {
      state.solutionId = action.payload.solutionId;
    },
    setStatusValidationError: (state) => {
      state.validationError = {
        hasError: true,
        validationErrorType: VALIDATION_ERROR_TYPE.STATUS,
      };
    },
    setMaterializeValidationError: (state) => {
      state.validationError = {
        hasError: true,
        validationErrorType: VALIDATION_ERROR_TYPE.MATERIALIZE,
      };
    },
    setSurveyConfigBlocksSelection: (
      state,
      action: PayloadAction<{
        surveyConfigId: string;
        selectedBlockIds: string[];
      }>,
    ) => {
      state.surveyConfigBlocksSelection = {
        ...state.surveyConfigBlocksSelection,
        [action.payload.surveyConfigId]: action.payload.selectedBlockIds,
      };
    },
    setSurveyConfigBlockGroupsSelection: (
      state,
      action: PayloadAction<{
        surveyConfigId: string;
        selectedGroupIds: string[];
      }>,
    ) => {
      state.surveyConfigBlockGroupsSelection = {
        ...state.surveyConfigBlockGroupsSelection,
        [action.payload.surveyConfigId]: action.payload.selectedGroupIds,
      };
    },
    unsetValidationError: (state) => {
      state.validationError = {
        hasError: false,
        validationErrorType: null,
      };
    },
    openBulkActionsMenu: (state) => {
      state.isBulkActionsMenuOpen = true;
    },
    closeBulkActionsMenu: (state) => {
      state.isBulkActionsMenuOpen = false;
    },
    setIsDirectBlueprintReset: (
      state,
      action: PayloadAction<{ isDirectBlueprintReset: boolean }>,
    ) => {
      state.isDirectBlueprintReset = action.payload.isDirectBlueprintReset;
    },
  },
});

export const {
  addDeletingSurveyConfigBlockGroupId,
  removeDeletingSurveyConfigBlockGroupId,
  closeBulkActionsMenu,
  clearSurveyConfigBlocksSelection,
  clearSurveyConfigBlockGroupsSelection,
  deleteSurveyConfigBlockDisplay,
  openBlockEditorTab,
  openBulkActionsMenu,
  setInputFocus,
  setInvalidSurveyConfigBlocks,
  setIsExpandedSurveyBundle,
  setIsExpandedSurveyConfigBlock,
  setIsProjectLocked,
  setIsSkipLogicLoading,
  setIsSurveyConfigValid,
  setOrderedSurveyConfigBlocks,
  setPreviewValidationError,
  setProjectId,
  setScrollToSurveyConfigBlockId,
  setSelectedSurveyConfigBlock,
  setSelectedSurveyConfigBlockGroup,
  setSolutionId,
  setStatusValidationError,
  setMaterializeValidationError,
  setSurveyConfigBlocksSelection,
  setSurveyConfigBlockGroupsSelection,
  unsetValidationError,
  setIsDirectBlueprintReset,
} = surveyDesignerSlice.actions;
